import React from 'react';
import { Box, Card, CardContent, Skeleton } from '@mui/material';
import { Typography } from '@mui/joy';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import _ from 'lodash';
import { AssetTelemetryAttributes } from '../../graphql/queries/assetTelemetryAttributes';
import { AssetNetworkInfo } from '../../graphql/queries/assetIoTConnectionWidget';
import TelemetryAttributeCard from '../TelemetryAttributeCard';
import AssetIoTConnection from '../AssetIoTConnection';
import AssetIoTStatus from '../AssetIoTStatus';

interface Props {
    assetLocationId: string;
    installedBaseNodeId?: string;
}

const AssetIoTTelemetry = ({ assetLocationId, installedBaseNodeId }: Props) => {
    const { t } = useTranslation();

    const telemetryQuery = useQuery(AssetTelemetryAttributes, {
        variables: { id: assetLocationId }
    });

    const networkQuery = useQuery(AssetNetworkInfo, {
        variables: { id: assetLocationId },
        // A failed network lookup nulls the whole iotConnection — keep it out of the cache.
        fetchPolicy: 'no-cache',
    });

    // Show skeleton cards while fetching
    if (telemetryQuery.loading) {
        return (
            <Box>
                {/* IoT Info Cards Skeleton */}
                <Box sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
                    gap: "24px",
                    marginBottom: "24px"
                }}>
                    {/* Connection Card Skeleton */}
                    <Card sx={{
                        border: "1px solid #cccccc",
                        borderRadius: "4px",
                        height: "100%",
                        boxShadow: "none"
                    }}>
                        <CardContent sx={{ padding: "20px" }}>
                            <Box sx={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "8px" }}>
                                <Skeleton variant="circular" width={28} height={28} />
                                <Skeleton variant="text" width={120} height={25} />
                            </Box>
                            <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: "3px", marginBottom: "8px" }} />
                            <Box sx={{ display: "flex", borderTop: "1px solid #cccccc", height: "260px", paddingTop: "16px", gap: "16px" }}>
                                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "20px" }}>
                                    <Skeleton variant="text" width="100%" height={22} />
                                    <Skeleton variant="circular" width={100} height={100} />
                                    <Skeleton variant="text" width={120} height={48} />
                                </Box>
                                <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "20px" }}>
                                    <Skeleton variant="text" width="100%" height={22} />
                                    <Skeleton variant="circular" width={100} height={100} />
                                    <Skeleton variant="text" width={80} height={48} />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Status Card Skeleton */}
                    <Card sx={{
                        border: "1px solid #cccccc",
                        borderRadius: "4px",
                        height: "100%",
                        boxShadow: "none"
                    }}>
                        <CardContent sx={{ padding: "20px" }}>
                            <Box sx={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "8px" }}>
                                <Skeleton variant="circular" width={28} height={28} />
                                <Skeleton variant="text" width={80} height={25} />
                            </Box>
                            <Skeleton variant="rectangular" width="100%" height={80} sx={{ borderRadius: "3px", marginBottom: "8px" }} />
                            <Box sx={{ borderTop: "1px solid #cccccc", padding: "16px 0" }}>
                                <Skeleton variant="text" width={180} height={22} sx={{ marginBottom: "20px" }} />
                                <Box sx={{ display: "flex", gap: "20px", alignItems: "center" }}>
                                    <Skeleton variant="rectangular" width="100%" height={8} sx={{ borderRadius: "4px" }} />
                                    <Skeleton variant="text" width={100} height={48} />
                                </Box>
                            </Box>
                            <Box sx={{ borderTop: "1px solid #cccccc", padding: "16px 0" }}>
                                <Skeleton variant="text" width={100} height={22} sx={{ marginBottom: "20px" }} />
                                <Box sx={{ display: "flex", gap: "20px", alignItems: "center" }}>
                                    <Skeleton variant="rectangular" width="100%" height={8} sx={{ borderRadius: "4px" }} />
                                    <Skeleton variant="text" width={100} height={48} />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                {/* Telemetry Cards Skeleton */}
                <Box sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))",
                    gap: "24px"
                }}>
                    {[...Array(6)].map((_, index) => (
                        <TelemetryAttributeCard
                            key={index}
                            name=""
                            value={null}
                            loading={true}
                        />
                    ))}
                </Box>
            </Box>
        );
    }

    // Extract telemetry attributes with safe access
    const attributes = _.get(telemetryQuery, 'data.assetLocation.telemetryAttributeList.attributes', []);
    const iotConnection = _.get(telemetryQuery, 'data.assetLocation.iotConnection');
    const signalStrength = _.get(networkQuery, 'data.assetLocation.iotConnection.networkInformation.signalStrength');

    // Extract health index from attributes
    const healthIndexAttr = attributes.find((attr: any) => attr.alias === 'HealthIndex');
    const healthIndex = _.get(healthIndexAttr, 'current.value');

    // Filter out health index from regular attributes to avoid duplication
    const filteredAttributes = attributes.filter((attr: any) => attr.alias !== 'HealthIndex');

    // Sort attributes alphabetically by name
    const sortedAttributes = [...filteredAttributes].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '')
    );

    return (
        <Box>
            {/* IoT Info Cards */}
            <Box sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
                gap: "24px",
                marginBottom: "24px"
            }}>
                <AssetIoTConnection
                    connectionStatus={iotConnection?.connectionStatus}
                    signalStrength={signalStrength}
                    networkLoading={networkQuery.loading}
                />
                <AssetIoTStatus
                    connectionStatus={iotConnection?.connectionStatus}
                    healthIndex={healthIndex}
                    remainingLifetime={286}
                />
            </Box>

            {/* Telemetry Attributes Grid */}
            {sortedAttributes.length === 0 ? (
                <Box sx={{ textAlign: "center", mt: 4 }}>
                    <Typography level="titleLarge" sx={{ color: '#666' }}>
                        {t('No telemetry data available')}
                    </Typography>
                </Box>
            ) : (
                <Box sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))",
                    gap: "24px"
                }}>
                    {sortedAttributes.map((attribute) => (
                        <TelemetryAttributeCard
                            key={attribute.id}
                            name={attribute.name}
                            value={_.get(attribute, 'current.value')}
                            unitSymbol={_.get(attribute, 'current.unit.symbol')}
                            unitName={_.get(attribute, 'current.unit.name')}
                            description={attribute.description}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default AssetIoTTelemetry;
